import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { get, omit } from 'lodash';
import { axiosService } from '../services/axiosService';

export interface BasicParams {
  config?: AxiosRequestConfig;
  body?: AxiosRequestConfig['data'];
  [key: string]: unknown;
}

export interface BasicParamsWithContentRange extends BasicParams {
  headers: {
    firstItemIndex: number;
    lastItemIndex: number;
  };
}

export interface ParamsWithId extends BasicParams {
  id: string;
}

export interface ParamsWithIds extends BasicParams {
  ids: string[];
}

export function createModel<ModelType>(
  basicUrlPath: string,
  isRestV2: boolean,
) {
  return {
    fetchById: async (args: ParamsWithId): Promise<ModelType> => {
      const { id, ...params } = args;
      const response = await axiosService.get<ModelType>(
        `${basicUrlPath}/${id}`,
        {
          params,
        },
      );
      return response.data;
    },

    fetchByIds: async (args: ParamsWithIds): Promise<ModelType[]> => {
      const { ids, ...params } = args;
      const response = await axiosService.get<{
        page: number;
        size: number;
        response: ModelType[];
      }>(`${basicUrlPath}${isRestV2 ? '' : '/crudList'}`, {
        params: { ...params, _id: ids.join(',') },
      });
      return response.data.response;
    },

    fetchMany: async (args: BasicParams): Promise<ModelType[]> => {
      const postFix = get(args, 'postFix', '');

      const response = await axiosService.get<{
        page: number;
        size: number;
        response: ModelType[];
      }>(`${basicUrlPath}${postFix ? `/${postFix}` : ''}`, {
        params: omit(args, 'postFix'),
      });
      return response.data.response;
    },

    fetchManyByContentRange: async (
      args: BasicParamsWithContentRange,
    ): Promise<{ data: ModelType[]; headers: AxiosResponse['headers'] }> => {
      const { postFix = '', headers } = args;

      const response = await axiosService.get<{
        response: ModelType[];
        headers: AxiosResponse['headers'];
      }>(`${basicUrlPath}${postFix ? `/${postFix}` : ''}`, {
        params: omit(args, 'headers', 'postFix'),
        headers: {
          'content-range': `${headers.firstItemIndex}-${headers.lastItemIndex}`,
        },
      });
      return { data: response.data.response, headers: response.headers };
    },

    count: async (args: BasicParams): Promise<number> => {
      const postFix = get(args, 'postFix', '');

      const response = await axiosService.get<{ count: number } | number>(
        `${basicUrlPath}${postFix ? `/${postFix}` : ''}/count`,
        { params: omit(args, 'postFix') },
      );

      if (typeof response.data === 'number') {
        return response.data;
      }

      return response.data.count;
    },

    create: async (args: BasicParams): Promise<ModelType> => {
      const { config, body } = args;
      const response = await axiosService.request<ModelType>({
        ...config,
        data: {
          ...get(config, 'data', {}),
          ...body,
        },
        url: basicUrlPath,
        method: 'post',
      });
      return response.data;
    },

    edit: async (args: BasicParams): Promise<ModelType> => {
      const { id, config, body } = args;
      const response = await axiosService.request<ModelType>({
        ...config,
        data: {
          ...get(config, 'data', {}),
          ...body,
        },
        url: `${basicUrlPath}/${id}`,
        method: 'put',
      });
      return response.data;
    },

    delete: async (args: BasicParams): Promise<ModelType> => {
      const { id, config, body } = args;
      const response = await axiosService.request<ModelType>({
        ...config,
        data: {
          ...get(config, 'data', {}),
          ...body,
        },
        url: `${basicUrlPath}/${id}`,
        method: 'delete',
      });
      return response.data;
    },

    deleteByIds: async (args: BasicParams): Promise<ModelType> => {
      const { ids, config, body } = args;
      const response = await axiosService.request<ModelType>({
        ...config,
        data: {
          ids,
          ...get(config, 'data', {}),
          ...body,
        },
        url: basicUrlPath,
        method: 'delete',
      });
      return response.data;
    },
  };
}
